import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * How one payment status is drawn.
 *
 * `chip` and `dot` are split because the WiServices status tokens are three roles that are not
 * interchangeable (see the palette note in `index.css`): `--<hue>` is the vivid fill and belongs to
 * dots and icons, `--<hue>-soft` is the tint behind a chip, and `--<hue>-ink` is the only step that
 * may be used for TEXT on that tint. Painting `text-<hue>` on `bg-<hue>-soft` is the trap the note
 * calls out — the two are the same hue at similar lightness and measured 1.96:1 to 3.23:1, below
 * WCAG AA. The `-ink` step clears AA in both themes.
 */
type PaymentBadgeConfig = {
  /** Human label. Backend statuses are PascalCase run together — "AwaitingVerification" reads as
      one long word in a narrow cell, so the words are split here rather than shown raw. */
  label: string;
  /** Tint, border and text for the chip itself. */
  chip: string;
  /** The 6px dot. `bg-<hue>` for a live state; a hollow ring for one that is over. */
  dot: string;
};

const NEUTRAL_CHIP = "border-hairline-strong bg-panel-2 text-ink-3";

const PAYMENT_STATUS_BADGE: Record<string, PaymentBadgeConfig> = {
  Completed: {
    label: "Completed",
    chip: "border-ok/30 bg-ok-soft text-ok-ink",
    dot: "bg-ok",
  },
  // The one row on this screen that is asking an operator for something, so it is the one that
  // carries the warn hue.
  AwaitingVerification: {
    label: "Awaiting Verification",
    chip: "border-warn/30 bg-warn-soft text-warn-ink",
    dot: "bg-warn",
  },
  // Waiting on the customer, not on us — informational rather than actionable.
  Pending: {
    label: "Pending",
    chip: "border-info/30 bg-info-soft text-info-ink",
    dot: "bg-info",
  },
  Failed: {
    label: "Failed",
    chip: "border-destructive/30 bg-destructive-soft text-destructive-ink",
    dot: "bg-destructive",
  },
  // Money moved back. Terminal and worth noticing, but nothing to act on.
  Refunded: {
    label: "Refunded",
    chip: "border-info/30 bg-info-soft text-info-ink",
    dot: "ring-1 ring-inset ring-info",
  },
  // Dead, not waiting: the hollow dot is what separates it from Pending at a glance, since both
  // are deliberately quiet.
  Cancelled: {
    label: "Cancelled",
    chip: NEUTRAL_CHIP,
    dot: "ring-1 ring-inset ring-ink-3",
  },
};

/** Splits a PascalCase status the map does not know into words, so a new backend value still reads. */
const humanize = (status: string) => status.replace(/([a-z0-9])([A-Z])/g, "$1 $2");

interface PaymentStatusBadgeProps {
  status: string;
  className?: string;
}

export function PaymentStatusBadge({ status, className }: PaymentStatusBadgeProps) {
  const config = PAYMENT_STATUS_BADGE[status] ?? {
    label: humanize(status),
    chip: NEUTRAL_CHIP,
    dot: "bg-ink-3",
  };

  return (
    // Quieter than the solid fills this used to draw: a saturated block the width of
    // "AwaitingVerification" outweighed the payment number it sits under, which is the row's actual
    // subject. A tint plus a dot carries the same colour coding at a fraction of the ink.
    // `variant="outline"` only so the base does not paint a background of its own; every colour
    // here comes from `chip`.
    <Badge
      variant="outline"
      className={cn(
        "gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium leading-4 shadow-none",
        config.chip,
        className,
      )}
    >
      <span className={cn("size-1.5 shrink-0 rounded-full", config.dot)} aria-hidden="true" />
      {config.label}
    </Badge>
  );
}
